import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton, Typography, TextField } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeKeyValueRow — Key + Value row with mention editors + delete button.
 * Used for HTTP headers, query parameters, etc.
 *
 * The caller provides custom renderKey/renderValue functions for flexibility
 * (can use NodeMentionEditor, plain TextField, etc.).
 *
 * Usage:
 *   <NodeKeyValueRow
 *     keyLabel="Header Name"
 *     valueLabel="Header Value"
 *     renderKey={(props) => (
 *       <TextField size="small" value={props.value} onChange={props.onChange} />
 *     )}
 *     renderValue={(props) => (
 *       <NodeMentionEditor value={props.value} onChange={props.onChange} />
 *     )}
 *     keyValue={item.key}
 *     valueValue={item.value}
 *     onKeyChange={(val) => updateKey(val)}
 *     onValueChange={(val) => updateValue(val)}
 *     onDelete={() => removeItem(index)}
 *   />
 */

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: NODE_UI.spacing.sm,
  width: '100%',
}));

const FieldContainer = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.3,
  marginBottom: '2px',
  userSelect: 'none',
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  marginTop: '18px', // Align with input fields when labels present
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: 'transparent',
  },
}));

const NodeKeyValueRow = React.memo(function NodeKeyValueRow({
  keyLabel = 'Key',
  valueLabel = 'Value',
  renderKey,
  renderValue,
  keyValue,
  valueValue,
  onKeyChange,
  onValueChange,
  onDelete,
  showLabels = true,
  showDelete = true,
  sx,
}) {
  const handleDelete = useCallback((e) => {
    stopPropagation(e);
    onDelete?.();
  }, [onDelete]);

  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      <FieldContainer>
        {showLabels && <Label>{keyLabel}</Label>}
        {renderKey
          ? renderKey({ value: keyValue, onChange: onKeyChange })
          : <TextField size="small" fullWidth value={keyValue || ''} onChange={(e) => onKeyChange?.(e.target.value)} placeholder={keyLabel} onMouseDown={stopPropagation} />
        }
      </FieldContainer>
      <FieldContainer>
        {showLabels && <Label>{valueLabel}</Label>}
        {renderValue
          ? renderValue({ value: valueValue, onChange: onValueChange })
          : <TextField size="small" fullWidth value={valueValue || ''} onChange={(e) => onValueChange?.(e.target.value)} placeholder={valueLabel} onMouseDown={stopPropagation} />
        }
      </FieldContainer>
      {showDelete && onDelete && (
        <DeleteButton size="small" onClick={handleDelete} onMouseDown={stopPropagation}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </DeleteButton>
      )}
    </Root>
  );
});

export default NodeKeyValueRow;
